import React, { forwardRef, useMemo } from "react";
import { downloadChatFile } from "../../../apis/chatApi";
import { useRoomDisplayMeta } from "../../../hooks/useRoomDisplayMeta";
import { getCurrentUserId } from "../../../utils/messageMapping";
import { countMembersNotReadMessage } from "../../../utils/readReceiptUtils";
import { WEBSOCKET_STATE } from "../../../websocket/webSocketState";
import ChatMessage from "../ChatMessage/ChatMessage";
import styles from "./ChatRoomMessageList.module.scss";

/**
 * 채팅방 상세 메시지 스레드 — 날짜 구분선, 그룹핑, 읽음 표시, ChatMessage 렌더
 */
const ChatRoomMessageList = forwardRef(function ChatRoomMessageList(
  {
    messages,
    room,
    listClassName,
    onOpenImagePreview,
    onFailedMessageRetry,
    onFailedMessageRemove,
    onSaleCardClick,
  },
  ref
) {
  const { displayName } = useRoomDisplayMeta(room);
  const nodes = useMemo(() => {
    const myId = getCurrentUserId();
    return messages.map((msg, i) => {
      const prev = messages[i - 1];
      const isSameGroup =
        prev != null &&
        prev.type === msg.type &&
        prev.time === msg.time &&
        prev.dateLabel === msg.dateLabel;
      const showReadReceipt =
        msg.type === "my" &&
        typeof msg.messageSeq === "number" &&
        msg.messageSeq > 0 &&
        Array.isArray(room?.members) &&
        room.members.length > 0;
      const unreadOthers = showReadReceipt
        ? countMembersNotReadMessage(msg.messageSeq, room.members, myId)
        : null;
      const readReceiptClass =
        unreadOthers != null ? (unreadOthers > 0 ? "unread" : "read") : undefined;
      const readReceiptCount = unreadOthers != null ? unreadOthers : undefined;

      const isMyFailed = msg.type === "my" && msg.status === WEBSOCKET_STATE.FAILED;

      return (
        <React.Fragment key={msg.clientRequestId ?? msg.id ?? i}>
          {msg.dateLabel != null &&
            (i === 0 || messages[i - 1]?.dateLabel !== msg.dateLabel) && (
              <div className={styles.dateSeparator} data-node-id="40004313:31148">
                {msg.dateLabel}
              </div>
            )}
          <ChatMessage
            type={msg.type}
            senderName={isSameGroup ? undefined : displayName}
            time={isSameGroup ? undefined : msg.time}
            grouped={isSameGroup}
            body={msg.body}
            avatarUrl={msg.avatarUrl}
            imageUrl={msg.imageUrl}
            imageUrls={msg.imageUrls}
            imageThumbnailUrls={msg.imageThumbnailUrls}
            imageFileNames={msg.imageFileNames}
            fileAttachments={msg.fileAttachments}
            fileName={msg.fileName}
            fileExtension={msg.fileExtension}
            fileUrl={msg.fileUrl}
            onDownloadFile={(url, name) => url && downloadChatFile(url, name)}
            onDownload={msg.fileUrl ? () => downloadChatFile(msg.fileUrl, msg.fileName) : undefined}
            onImageClick={
              msg.imageUrls?.length > 0 || msg.imageUrl ? onOpenImagePreview : undefined
            }
            content={msg.content}
            status={msg.status}
            readReceiptClass={readReceiptClass}
            readReceiptCount={readReceiptCount}
            onFailedRetry={
              isMyFailed ? () => onFailedMessageRetry?.(msg) : undefined
            }
            onFailedDelete={
              isMyFailed ? () => onFailedMessageRemove?.(msg) : undefined
            }
            onSaleCardClick={onSaleCardClick}
            failReason={msg.failReason}
          />
        </React.Fragment>
      );
    });
  }, [
    messages,
    displayName,
    onOpenImagePreview,
    room,
    onFailedMessageRetry,
    onFailedMessageRemove,
    onSaleCardClick,
  ]);

  return (
    <div ref={ref} className={listClassName}>
      {nodes}
    </div>
  );
});

export default ChatRoomMessageList;
